/**
 * iter87.133 — First-Time Crewmate Consent Modal.
 *
 * Popup gate for regular (non-admin) crewmates joining a Rivalry for the
 * first time via magic-link OR landing-page /leagues/join. Enforces two
 * explicit consents:
 *   1. "I am 18 or older" (age gate — separate consent from T&C)
 *   2. "I accept the Terms & Conditions" (links out to /terms)
 *
 * Contract:
 *   - `open` — controls visibility.
 *   - `leagueName` — displayed in the header ("Join <name>").
 *   - `onAccept()` — invoked when both boxes are checked and user clicks
 *     "Enter Rivalry". Caller is responsible for sending
 *     `{accepted_tc: true, age_confirmed: true}` on the follow-up request.
 *   - `onDecline()` — invoked when user clicks "Decline". Caller should
 *     surface a refusal screen (see Landing.jsx / JoinPage.jsx).
 *
 * Design notes (iter87.133):
 *   - Age gate is FIRST checkbox (higher legal weight than T&C).
 *   - "Enter Rivalry" is DISABLED until both boxes are checked — no
 *     silent submit-then-reject bounce.
 *   - Terms + Privacy links open in a new tab so the modal state is not
 *     lost mid-review.
 *   - Refusal is an explicit user action (Decline button), NOT an X /
 *     click-outside dismiss — click-outside/Escape are no-ops and the
 *     X close button is removed entirely (iter87.212.7 — it used to
 *     render but silently do nothing, which read as broken).
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ExternalLink, Shield } from "lucide-react";

export default function FirstTimeConsentModal({
    open,
    leagueName,
    onAccept,
    onDecline,
    submitting = false,
    // iter87.199.99 — Context-aware CTA label. Default "Enter Rivalry"
    // fits the crewmate-joining-a-league path. On the Pro+ Clerk
    // sign-up path we pass "Continue sign-up" so the button reflects
    // the actual next step. On any other embed, pass whatever you like.
    acceptLabel = "Enter Rivalry",
    submittingLabel = "Joining…",
    // iter87.419 (user-requested) — the /start flow (creating a brand
    // new Rivalry) has no `leagueName` yet, so it fell back to the
    // "Join this Rivalry" copy meant for the /join flow. `title`
    // overrides the header entirely for non-join embeds; leave unset
    // to keep the existing "Join <name>" / "Join this Rivalry" copy.
    title,
}) {
    const [ageOk, setAgeOk] = useState(false);
    const [tcOk, setTcOk] = useState(false);

    // Reset on close — a subsequent open should not preserve prior checks.
    useEffect(() => {
        if (!open) {
            setAgeOk(false);
            setTcOk(false);
        }
    }, [open]);

    const canAccept = ageOk && tcOk && !submitting;

    return (
        <Dialog
            open={open}
            // Explicit gate: X / click-outside must NOT close. Only the
            // Decline button closes the modal (via onDecline handler).
            onOpenChange={() => {}}
        >
            <DialogContent
                data-testid="first-time-consent-modal"
                className="brutal-card max-w-md p-0 border-2 border-cyber overflow-hidden"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                hideCloseButton
            >
                <div className="p-6 space-y-5">
                    <DialogHeader className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-cyber border-2 border-black shadow-brutalYellow flex items-center justify-center shrink-0">
                                <Shield className="w-5 h-5 text-black" strokeWidth={2.5} />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                                    Before you enter
                                </p>
                                <DialogTitle className="font-display font-black text-lg text-white leading-tight">
                                    {title ? title : (leagueName ? <>Join <span className="text-cyber">{leagueName}</span></> : "Join this Rivalry")}
                                </DialogTitle>
                            </div>
                        </div>
                        <DialogDescription className="text-xs text-zinc-400 leading-relaxed">
                            Tiny Crew Rivalries is an entertainment-only pick'em platform.
                            Confirm both to continue.
                        </DialogDescription>
                    </DialogHeader>

                    <label className="flex items-start gap-3 text-xs text-zinc-300 cursor-pointer">
                        <input
                            data-testid="consent-age-checkbox"
                            type="checkbox"
                            checked={ageOk}
                            onChange={(e) => setAgeOk(e.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-cyber"
                        />
                        <span>
                            <strong className="text-white">I am 18 years of age or older.</strong>{" "}
                            <span className="text-zinc-500">
                                This platform is not intended for minors.
                            </span>
                        </span>
                    </label>

                    <label className="flex items-start gap-3 text-xs text-zinc-300 cursor-pointer">
                        <input
                            data-testid="consent-tc-checkbox"
                            type="checkbox"
                            checked={tcOk}
                            onChange={(e) => setTcOk(e.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-cyber"
                        />
                        <span>
                            I have read and accept the{" "}
                            <a
                                href="/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyber underline inline-flex items-center gap-0.5"
                                data-testid="consent-terms-link"
                            >
                                Terms & Conditions <ExternalLink className="w-3 h-3" />
                            </a>{" "}
                            and{" "}
                            <a
                                href="/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyber underline inline-flex items-center gap-0.5"
                                data-testid="consent-privacy-link"
                            >
                                Privacy Policy <ExternalLink className="w-3 h-3" />
                            </a>.
                        </span>
                    </label>

                    <div className="flex gap-2 pt-2">
                        <button
                            type="button"
                            data-testid="consent-decline-btn"
                            onClick={onDecline}
                            disabled={submitting}
                            className="brutal-btn text-xs border-zinc-600 text-zinc-300 flex-1 disabled:opacity-40"
                        >
                            Decline
                        </button>
                        <button
                            type="button"
                            data-testid="consent-accept-btn"
                            onClick={onAccept}
                            disabled={!canAccept}
                            className="brutal-btn bg-cyber text-black font-display font-black text-sm flex-[2] disabled:opacity-40 disabled:bg-zinc-700 disabled:text-zinc-500"
                        >
                            {submitting ? submittingLabel : acceptLabel}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
